import type { Json } from "@/integrations/supabase/types";
import {
  FISHSIX_SURVEY_VERSION,
  type FishsixSurveyResponsesV1,
  type FishSurveyOption,
} from "@/utils/fishsixSurveyConstants";

export function fishsixOptionLabel(id: string, options: FishSurveyOption[]): string {
  return options.find((o) => o.id === id)?.label ?? id;
}

export function parseFishsixV1Responses(raw: Json | null | undefined): FishsixSurveyResponsesV1 | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (o.version !== FISHSIX_SURVEY_VERSION) return null;

  const num = (v: unknown): number => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const strArr = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

  return {
    version: FISHSIX_SURVEY_VERSION,
    s1_academic_development: num(o.s1_academic_development),
    s1_clear_outcomes: strArr(o.s1_clear_outcomes),
    s1_emphasize_more: strArr(o.s1_emphasize_more),
    s2_teacher_quality: num(o.s2_teacher_quality),
    s2_teacher_strengths: strArr(o.s2_teacher_strengths),
    s2_teacher_improve: strArr(o.s2_teacher_improve),
    s3_admin_service: num(o.s3_admin_service),
    s3_admin_strengths: strArr(o.s3_admin_strengths),
    s3_admin_improve: strArr(o.s3_admin_improve),
    s4_facility_atmosphere: num(o.s4_facility_atmosphere),
    s4_facility_strengths: strArr(o.s4_facility_strengths),
    s4_facility_improve: strArr(o.s4_facility_improve),
    s5_additional: typeof o.s5_additional === "string" ? o.s5_additional : "",
  };
}

/** Mean of valid 1–10 section scores, or null if none. */
export function averageFishsixSurveyScore(r: FishsixSurveyResponsesV1): number | null {
  const vals = [
    r.s1_academic_development,
    r.s2_teacher_quality,
    r.s3_admin_service,
    r.s4_facility_atmosphere,
  ].filter((n) => typeof n === "number" && n >= 1 && n <= 10);
  if (!vals.length) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
}
